import React, { useCallback, useContext, useState } from "react";
import InterfaceContext from "../Context";
import { useLocalizedStrings } from "../Localization";
import { RigidSpinner, languageContent, safeParse } from "../Util";
import { QuestionProps } from "./Props";
import * as Sentry from "@sentry/react";
import { Button, useToast } from "@aidkitorg/component-library";
import { getURLsFromCSV } from "@aidkitorg/roboscreener/lib/util/urls_from_csv";
import { useDownloader, usePost } from "../API";
import type { BulkDownload, RichText, Text, VisitLink } from "@aidkitorg/types/lib/survey";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { useModularMarkdown } from "../Hooks/ModularMarkdown";
import * as v0 from "@aidkitorg/types/lib/survey";

async function loadFFlate() {
  return await import('fflate');
}

export function ActionButton(props: QuestionProps) {
  const prev = (props.info[props["Target Field"]!] || '').match(/^(01|02|03|04|05|06|07|08|09|10|11|12)\/([0-3][0-9])\/((19|20)[0-9][0-9])$/);
  const context = useContext(InterfaceContext);
  const L = useLocalizedStrings();
  const content = props[languageContent(context.lang)];
  const metadata = safeParse(props["Metadata"]!) as {
    // ID for getting the action definition from the backend
    id?: string,
    action: v0.Action['action'],
    confirmation: Text,
    toast: Text,
    explanation?: RichText,
  };
  const action = metadata.action;

  const getSignedURL = usePost('/document/view');
  const { toast } = useToast();
  const downloadFile = useDownloader();
  const createUserFromApplicant = usePost('/organization/create_user_from_applicant');

  const [loadingDownload, setLoadingDownload] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const marked = useModularMarkdown({ content: metadata.explanation || '', info: props.info });

  let func: Function | undefined;
  if (action && typeof action === 'string') {
    try {
      func = Function(
        "return (function(info, org, screener) { const out = " + action + "; return out });"
      )();
    } catch (err: any) {
      console.log('Invalid Action formula. Error:', err);
    }
  }

  const loadAttachments = (targetField: string) => {
    return getURLsFromCSV(props.info[targetField] || "")
      .reduce((acc, url: string) => {
        acc[url] = true;
        return acc;
      }, {} as Record<string, boolean>);
  };

  async function doBulkDownload() {
    if (typeof action === 'string' || action.kind !== 'Bulk Download') return;

    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().slice(0, 10); // YYYY-MM-DD
    const formattedTime = currentDate.toTimeString().slice(0, 8).replace(/:/g, '-'); // HH-MM-SS
    const folderName = `${action.fileName || 'downloads'}_${formattedDate}_${formattedTime}`;

    const zipContents = {} as any;
    setLoadingDownload(true);
    const downloadFields = action.fields;
    for (const field of downloadFields) {
      // target could be a list of options. 
      const loadedURLS = loadAttachments(field);
      const res = await getSignedURL({
        paths: Object.keys(loadedURLS),
      });

      const urls = Object.values(res.paths);
      for (let index = 0; index < urls.length; index++) {
        const url = urls[index];
        const response = await fetch(url);
        const arrayBuffer = await response.arrayBuffer();
        zipContents[`${field}${index + 1}.jpg`] = new Uint8Array(arrayBuffer);
      }
    }

    const fflate = await loadFFlate();
    const zippedData = fflate.zipSync(zipContents);
    const blob = new Blob([zippedData], { type: 'application/ zip' });
    const url = window.URL.createObjectURL(blob);
    downloadFile(url, `${folderName}.zip`);

    setLoadingDownload(false);
  }

  async function doAction() {
    if (metadata.confirmation) {
      const sure = window.confirm(metadata.confirmation[context.lang || 'en'] || 'Are you sure you wish to continue?');
      if (!sure) return;
    }
    try {
      // Visit External Link
      if (action && (action as VisitLink)?.kind === 'Visit External Link') {
        window.open((action as VisitLink).url, '_blank');
        if ((action as VisitLink).targetField) {
          props.setInfoKey((action as VisitLink).targetField!, new Date().toISOString(), true, false);
        }

      // Custom Action Button
      } else if (typeof action === 'string') {
        if (!func) throw new Error(`No action function`);

        const r: Record<string, any> = func(props.info, props.orgMetadata, props.screenerMetadata) || {};
        for (const key in r) {
          props.setInfoKey(key, (r[key] || '') + '', true, false);
        }
      } else if (action?.kind === 'Create User From Applicant') {
        // This should not happen unless someone modifies an existing action button to add this new type.
        if (!metadata.id) throw new Error('No action ID Found. Please delete the action and recreate it.');

        // Call the API to create the org, user, and user group
        const res = await createUserFromApplicant({ actionId: metadata.id!, appInfo: props.info, applicantUid: props.uid! });
        if (res && 'error' in res && res.error === 'User already exists.') {
          setDisabled(true);
        }
      }

      if (metadata.toast) {
        toast({
          description: metadata.toast[context.lang || 'en'] || 'Success!',
          variant: 'success'
        });
      }
    } catch (e) {
      console.log("Error computing: ", e);
      alert("Error: " + e);
      Sentry.captureException(e, { 
        extra: {
          action
        }
      });
    }
  }

  return <div className="my-2">
    {marked && <div>{marked}</div>}
    <Button
      variant="primary"
      disabled={disabled}
      onClick={action && (action as BulkDownload)?.kind === 'Bulk Download' ? doBulkDownload : doAction}
    >
      {content}{action && (action as VisitLink)?.kind === 'Visit External Link' 
        ? <ArrowTopRightOnSquareIcon className="h-5 w-5 ml-2" /> : ''}
    </Button>
    {loadingDownload ? <RigidSpinner /> : <></>}
  </div>
}